import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowUpRight, ArrowLeft } from 'lucide-react';
import Reveal from '../components/Reveal';
import Seo from '../components/Seo';
import { caseStudies, businesses } from '../mockData';

const CaseStudyDetailPage = () => {
  const { slug } = useParams();
  const cs = caseStudies.find((c) => c.slug === slug);

  if (!cs) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-alabaster">
        <div className="text-center">
          <div className="eyebrow mb-4">404</div>
          <h1 className="font-display text-5xl text-brand-oxford mb-6">Case study not found</h1>
          <Link to="/case-studies" className="text-brand-oxford border-b border-brand-oxford pb-1">Back to case studies</Link>
        </div>
      </div>
    );
  }

  const business = businesses.find((b) => b.id === cs.business);
  const accent = `rgb(var(--sub-${business?.subBrand || 'crm'}))`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: cs.title,
    description: cs.excerpt,
    datePublished: cs.date,
    articleSection: cs.category,
    image: `https://lockene.info${cs.image}`,
    publisher: { '@type': 'Organization', name: 'Lockene' },
    author: { '@type': 'Organization', name: 'Lockene' }
  };

  return (
    <>
      <Seo
        title={`${cs.title} · Lockene Case Study`}
        description={cs.excerpt}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="bg-brand-alabaster pt-28 md:pt-36 pb-16" aria-labelledby="cs-detail-heading" data-testid={`cs-detail-hero-${cs.slug}`}>
        <div className="container-editorial">
          <div className="border-t border-brand-hairline mb-14" />
          <div className="max-w-4xl">
            <Link
              to="/case-studies"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.16em] uppercase font-bold text-brand-slate hover:text-brand-oxford transition-colors mb-8"
              data-testid="cs-detail-back"
            >
              <ArrowLeft size={14} strokeWidth={1.5} /> All Case Studies
            </Link>
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block w-10 h-px" style={{ backgroundColor: accent }} aria-hidden />
              <span className="text-[11px] tracking-[0.18em] uppercase font-bold" style={{ color: accent }}>
                {cs.category} · <time dateTime={cs.date}>{new Date(cs.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</time>
              </span>
            </div>
            <h1 id="cs-detail-heading" className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight text-brand-oxford">
              {cs.title}
            </h1>
            <p className="mt-8 text-lg md:text-xl text-brand-slate leading-relaxed max-w-3xl">
              {cs.excerpt}
            </p>
            <div className="mt-10 text-sm font-semibold text-brand-oxford">{cs.results}</div>
          </div>
        </div>
      </section>

      {/* Facts strip */}
      <section className="bg-white border-y border-brand-hairline py-10" aria-label="Engagement facts">
        <div className="container-editorial grid grid-cols-2 md:grid-cols-4 gap-y-6">
          {[
            { l: 'Client', v: cs.client },
            { l: 'Industry', v: cs.industry },
            { l: 'Engagement', v: cs.engagementLength },
            { l: 'Practice', v: cs.category }
          ].map((f, i) => (
            <div key={f.l} className={i !== 0 ? 'md:border-l md:border-brand-hairline md:pl-8' : ''}>
              <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-brand-slate/70">{f.l}</div>
              <div className="font-display text-xl text-brand-oxford mt-2 leading-tight">{f.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Hero image */}
      <section className="bg-brand-alabaster">
        <div className="container-editorial py-14">
          <div className="relative">
            <div className="absolute -top-3 -left-3 w-24 h-24 border pointer-events-none" style={{ borderColor: accent, opacity: 0.4 }} aria-hidden />
            <img src={cs.image} alt={cs.title} className="relative w-full max-h-[560px] object-cover border border-brand-hairline" />
            <div className="absolute -bottom-4 -right-4 w-24 h-24 border border-brand-gold pointer-events-none" aria-hidden />
          </div>
        </div>
      </section>

      {/* Narrative */}
      <section className="section bg-white border-t border-brand-hairline" aria-labelledby="cs-narrative-heading">
        <div className="container-editorial grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-6">
              <span className="hairline" aria-hidden />
              <span className="eyebrow">The Mandate</span>
            </div>
            <h2 id="cs-narrative-heading" className="font-display text-3xl md:text-4xl leading-tight text-brand-oxford sticky top-28">
              How the work unfolded.
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-12">
            {cs.sections.map((s, i) => (
              <Reveal key={s.heading} delay={i * 60}>
                <div>
                  <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-brand-slate/80 mb-3">
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl text-brand-oxford leading-tight mb-4">{s.heading}</h3>
                  <p className="text-lg text-brand-slate leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Metrics */}
      {cs.metrics && (
        <section className="section bg-brand-alabaster" aria-labelledby="metrics-heading" data-testid="cs-metrics">
          <div className="container-editorial">
            <Reveal className="max-w-2xl mb-14">
              <div className="flex items-center gap-3 mb-6">
                <span className="hairline" aria-hidden />
                <span className="eyebrow">Documented Outcomes</span>
              </div>
              <h2 id="metrics-heading" className="font-display text-4xl md:text-5xl leading-tight text-brand-oxford">
                Numbers before, numbers after.
              </h2>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {cs.metrics.map((m, i) => (
                <Reveal key={m.label} delay={i * 60}>
                  <div className="bg-white border border-brand-hairline p-8">
                    <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-brand-slate/80">{m.label}</div>
                    <div className="mt-4 flex items-baseline gap-6 flex-wrap">
                      <div>
                        <div className="text-[11px] tracking-[0.14em] uppercase text-brand-slate/70">Before</div>
                        <div className="font-display text-3xl text-brand-slate line-through decoration-brand-hairline">{m.before}</div>
                      </div>
                      <div className="w-6 h-px bg-brand-oxford" aria-hidden />
                      <div>
                        <div className="text-[11px] tracking-[0.14em] uppercase font-bold" style={{ color: accent }}>After</div>
                        <div className="font-display text-4xl text-brand-oxford">{m.after}</div>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Timeline */}
      {cs.timeline && (
        <section className="section bg-white border-y border-brand-hairline" aria-labelledby="timeline-heading">
          <div className="container-editorial grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="flex items-center gap-3 mb-6">
                <span className="hairline" aria-hidden />
                <span className="eyebrow">Timeline</span>
              </div>
              <h2 id="timeline-heading" className="font-display text-3xl md:text-4xl leading-tight text-brand-oxford">
                Documented phase by phase.
              </h2>
            </div>
            <div className="lg:col-span-8">
              <ol className="relative border-l border-brand-hairline pl-8 space-y-10">
                {cs.timeline.map((t, i) => (
                  <li key={t.phase} className="relative">
                    <span
                      aria-hidden
                      className="absolute -left-[38px] top-1 w-3 h-3"
                      style={{ backgroundColor: accent }}
                    />
                    <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-brand-slate/80">{t.duration}</div>
                    <h3 className="font-display text-2xl text-brand-oxford mt-2">{t.phase}</h3>
                    <p className="mt-3 text-brand-slate leading-relaxed">{t.summary}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-brand-oxford text-brand-alabaster py-24 md:py-32">
        <div className="container-editorial grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block w-10 h-px bg-brand-gold" aria-hidden />
              <span className="eyebrow text-brand-gold/90">Ready to discuss a similar mandate?</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.1]">
              A senior advisor will respond within one business day.
            </h2>
          </div>
          <div className="lg:col-span-4 lg:text-right flex flex-col gap-3 lg:items-end">
            <Link
              to={`/business/${cs.business}`}
              data-testid="cs-detail-visit-business"
              className="inline-flex items-center gap-3 border border-brand-alabaster text-brand-alabaster px-6 py-4 text-[12px] font-semibold tracking-[0.16em] uppercase hover:bg-brand-alabaster hover:text-brand-oxford transition-colors"
            >
              Visit {business?.name || 'business'} <ArrowUpRight size={14} strokeWidth={1.5} />
            </Link>
            <Link
              to="/contact"
              data-testid="cs-detail-cta"
              className="inline-flex items-center gap-3 bg-brand-alabaster text-brand-oxford px-6 py-4 text-[12px] font-semibold tracking-[0.16em] uppercase hover:bg-brand-gold transition-colors"
            >
              Speak with Lockene <span aria-hidden>→</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default CaseStudyDetailPage;
